// src/services/auth/authService.js
import api from '../api';

class AuthService {
  async login(email, password) {
    try {
      const response = await api.post('login/', { email, password });
      if (response.data.success) {
        // Stockage des tokens JWT
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        this.setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      return { success: false, error: 'Réponse inattendue du serveur', field: 'general' };
    } catch (err) {
      return this.handleError(err);
    }
  }

  async logout() {
    try {
      await api.post('logout/');
    } catch (err) {
      console.error("Erreur lors du logout:", err);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  getUserRole() {
    const user = this.getCurrentUser();
    return user ? user.role : null;
  }

  // ── Rôles ──
  //
  // Ces helpers servent à ne PAS afficher un lien qui mènerait à un 403 : ils
  // améliorent l'expérience, ils ne protègent rien. La vraie autorisation est
  // dans backend/core/permissions.py, et elle doit rester la seule référence.
  // Le Lab, lui, ne contrôlait les rôles QUE dans son garde Angular : un appel
  // HTTP direct suffisait à tout contourner.

  hasRole(...roles) {
    return roles.includes(this.getUserRole());
  }

  isSuperAdmin() {
    return this.getUserRole() === 'super_admin';
  }

  /** Administration du centre. */
  isGestion() {
    return this.hasRole('super_admin', 'admin');
  }

  /** Prospects, diplômés, campagnes. */
  isCommercial() {
    return this.hasRole('super_admin', 'admin', 'agent_marketing');
  }

  /**
   * Formations, sessions et créneaux du planning (PlanifieLesFormations) :
   * l'assistante (agent marketing) les gère aussi. Les suppressions restent
   * à la gestion (isGestion).
   */
  peutPlanifier() {
    return this.hasRole('super_admin', 'admin', 'agent_marketing');
  }

  /** Présences et notes. */
  isPedagogique() {
    return this.hasRole('super_admin', 'admin', 'formateur');
  }

  isFormateur() {
    return this.getUserRole() === 'formateur';
  }

  isApprenant() {
    return this.getUserRole() === 'apprenant';
  }

  isAuthenticated() {
    return !!this.getCurrentUser() && !!localStorage.getItem('access_token');
  }

  handleError(err) {
    if (err.response?.data) {
      const data = err.response.data;
      if (data.field) {
        return {
          success: false,
          error: data.error,
          field: data.field,
          errorType: data.error_type
        };
      }
      switch (err.response.status) {
        case 400:
          return { success: false, error: data.error || 'Données invalides', field: 'general' };
        case 401:
          return { success: false, error: data.error || 'Email ou mot de passe incorrect', field: 'general' };
        case 403:
          return { success: false, error: 'Accès interdit', field: 'general' };
        case 404:
          return { success: false, error: data.error || 'Ressource non trouvée', field: 'general' };
        case 500:
          return { success: false, error: 'Erreur serveur. Réessayez plus tard.', field: 'general' };
        default:
          return { success: false, error: data.error || 'Erreur de connexion', field: 'general' };
      }
    }
    if (err.code === 'ERR_NETWORK') {
      return {
        success: false,
        error: 'Serveur non accessible. Vérifiez que Django est lancé.',
        field: 'general'
      };
    }
    return { success: false, error: err.message || 'Erreur inconnue', field: 'general' };
  }
}

export default new AuthService();